<template>
  <div id="app">
    <v-app id="inspire">
      <v-container style="min-height: 100%">
        <router-view :key="$route.fullPath"></router-view>
      </v-container>
      <Loading ref="loading" />
      <IdleWarningDialog ref="idleWarning" />
    </v-app>
  </div>
</template>

<script>
import Loading from '@/components/Loading'
import utilities from "./assets/js/utilities";
import {Auth} from "aws-amplify";
import IdleWarningDialog from "./components/dialog/IdleWarningDialog.vue";
import IdleMonitor, { clearLastActivityAt } from "./assets/js/idleMonitor";

/** tabId -> last ping timestamp (ms). Survives across tabs; stale entries = closed/crashed tabs. */
const TAB_META_KEY = 'app_cognito_tab_meta'
/** Legacy array format — remove if present. */
const LEGACY_TAB_IDS_KEY = 'app_cognito_tab_ids'
const PING_INTERVAL_MS = 8000
/**
 * Other tabs: no ping for this long ⇒ treat as dead (browser / tab hard-killed; setTimeout on unload is unreliable).
 * If the user re-opens the site sooner than this, they may still appear logged in until pings expire.
 */
const TAB_STALE_MS = 25000
/** After pagehide empties tab meta, wait before clearing Cognito so a reloading tab can register again. */
const DEFERRED_LAST_TAB_SIGNOUT_MS = 500

export default {
  name: 'App',
  components: {
    IdleWarningDialog,
    Loading,
  },
  data() {
    return {
      idleMonitor: null,
      tabId: null,
      heartbeatTimer: null,
      deferredSignOutTimer: null,
    }
  },
  methods: {
    clearAuthData(reason = 'IDLE_TIMEOUT') {
      console.log('clearAuthData');
      const doSignOut = () => {
        try {
          for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i)
            if (key && key.includes('CognitoIdentityServiceProvider')) {
              sessionStorage.removeItem(key)
            }
          }
          sessionStorage.removeItem('app_login_time')
          clearLastActivityAt()
        } catch (e) { /* ignore */ }

        Auth.signOut({ global: true })
          .then(() => this.$router.replace('/login'))
          .catch(err => console.error('error:', err));
      }

      utilities.createUserLogoutRecord(reason)
        .catch(() => {})
        .finally(() => doSignOut())
    },

    clearCognitoLocalStorageSync() {
      // Keep legacy method name; now clears per-tab sessionStorage.
      try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i)
          if (key && key.includes('CognitoIdentityServiceProvider')) {
            sessionStorage.removeItem(key)
          }
        }
        sessionStorage.removeItem('app_login_time')
        clearLastActivityAt()
      } catch (e) { /* ignore */ }
    },

    destroyIdleMonitor() {
      if (this.idleMonitor) {
        this.idleMonitor.destroy()
        this.idleMonitor = null
      }
      if (this.$refs.idleWarning) {
        this.$refs.idleWarning.hide()
      }
    },

    computeIdleTimeout(userType) {
      if (userType >= 3 && userType !== 5 && userType !== 7) {
        return 240 * 60 * 1000
      }
      return 60 * 60 * 1000
    },

    async startIdleMonitor(sub) {
      this.destroyIdleMonitor()
      if (!sub) {
        return
      }

      let user = this.$store.getters['user/getUser']
      if (!user || !user.id) {
        const userToSave = await utilities.getUserBySub(sub)
        if(userToSave.type === 7) {
          const res = await utilities.getPracticeManagerProfile()
          const specialists = res.Specialists
          if(specialists.find(item => item.PracticeManagerSpecialist.Selected)) {
            await this.$store.dispatch('user/setUser', {...userToSave, specialistId: specialists.find(item => item.PracticeManagerSpecialist.Selected).SpecialistID, list: specialists} )
          }
        } else {
          await this.$store.dispatch('user/setUser', userToSave)
        }
        user = userToSave
      }

      const idleTimeout = this.computeIdleTimeout(user.type)

      const warningTimeout = 5 * 60 * 1000

      this.idleMonitor = new IdleMonitor({
        idleTimeout,
        warningTimeout,
        onIdleWarning: (remainingSeconds, actions) => {
          this.$refs.idleWarning.show(remainingSeconds, {
            continue: async () => {
              try {
                await Auth.currentSession()
                actions.continue()
              } catch (e) {
                console.warn('Session no longer valid on continue', e)
                this.$refs.idleWarning.hide()
                this.clearAuthData('SESSION_INVALID')
              }
            },
            logout: () => actions.logout(),
          })
        },
        onHideWarning: () => {
          if (this.$refs.idleWarning) {
            this.$refs.idleWarning.hide()
          }
        },
        onLogout: () => {
          if (this.$refs.idleWarning) {
            this.$refs.idleWarning.hide()
          }
          this.clearAuthData()
        },
      })

      this.idleMonitor.start()
    },

    resetVuexUserIfPossible() {
      try {
        this.$store.dispatch('user/setAuthentication', utilities.initAuthentication())
        this.$store.dispatch('user/setUser', utilities.initUser())
      } catch (e) { /* ignore */ }
    },

    getTabMeta() {
      try {
        const raw = localStorage.getItem(TAB_META_KEY)
        return raw ? JSON.parse(raw) : {}
      } catch (e) {
        return {}
      }
    },

    setTabMeta(meta) {
      if (!Object.keys(meta).length) {
        localStorage.removeItem(TAB_META_KEY)
      } else {
        localStorage.setItem(TAB_META_KEY, JSON.stringify(meta))
      }
    },

    /**
     * Drop entries with no heartbeat, except optionally keep current tab (not yet in map).
     */
    pruneStaleTabMeta() {
      const now = Date.now()
      const meta = this.getTabMeta()
      const cur = this.tabId
      let changed = false
      Object.keys(meta).forEach((id) => {
        if (cur && id === cur) {
          return
        }
        if (now - meta[id] > TAB_STALE_MS) {
          delete meta[id]
          changed = true
        }
      })
      if (changed) {
        this.setTabMeta(meta)
      }
      return meta
    },

    touchTabMeta() {
      if (!this.tabId) {
        return
      }
      // Always re-read: another tab may have written since we last read.
      const meta = this.getTabMeta()
      meta[this.tabId] = Date.now()
      this.setTabMeta(meta)
    },

    removeCurrentTabFromMeta() {
      if (!this.tabId) {
        return this.getTabMeta()
      }
      const meta = this.getTabMeta()
      delete meta[this.tabId]
      this.setTabMeta(meta)
      return meta
    },

    /** Run after unload: if no tab re-registered in time, Cognito was for a closed session. */
    scheduleDeferredSignOutIfNoTabs() {
      if (this.deferredSignOutTimer) {
        clearTimeout(this.deferredSignOutTimer)
      }
      this.deferredSignOutTimer = setTimeout(() => {
        this.deferredSignOutTimer = null
        this.pruneStaleTabMeta()
        // Cognito session lives in sessionStorage (per-tab), so refresh/tab-close
        // shouldn't require explicit signOut/clearing here.
      }, DEFERRED_LAST_TAB_SIGNOUT_MS)
    },

    onPageHide(event) {
      if (event.persisted) {
        return
      }
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer)
        this.heartbeatTimer = null
      }
      this.removeCurrentTabFromMeta()
      const meta = this.pruneStaleTabMeta()
      // Last tab closed: stash a pending logout record.
      // Reported on next visit via flushPendingTabCloseLogout (refresh keeps session → skipped).
      if (Object.keys(meta).length === 0) {
        utilities.markPendingTabCloseLogout('ALL_TABS_CLOSED')
      }
    },

    startTabHeartbeat() {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer)
      }
      this.touchTabMeta()
      this.heartbeatTimer = setInterval(() => {
        this.pruneStaleTabMeta()
        this.touchTabMeta()
      }, PING_INTERVAL_MS)
    },

    onPageShow(event) {
      if (event.persisted) {
        this.pruneStaleTabMeta()
        this.startTabHeartbeat()
      }
    },
  },
  mounted() {
    localStorage.removeItem(LEGACY_TAB_IDS_KEY)

    // If last tab was refreshed (session still alive), discard pending tab-close logout.
    // Actual ALL_TABS_CLOSED report happens after the same user logs in again.
    utilities.discardPendingTabCloseLogoutIfSessionAlive().catch(() => {})

    try {
      let id = sessionStorage.getItem('app_tab_id')
      if (!id) {
        id = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `tab_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        sessionStorage.setItem('app_tab_id', id)
      }
      this.tabId = id
      this.pruneStaleTabMeta()
      // Re-register this tab first so refresh / slow loads do not see an empty meta and trigger sign-out.
      this.touchTabMeta()
      this.startTabHeartbeat()
    } catch (e) {
      console.warn('Tab meta unavailable:', e)
    }

    window.addEventListener('pagehide', this.onPageHide)
    window.addEventListener('pageshow', this.onPageShow)

    this.$watch(
      () => this.$store.getters['user/getAuthentication']?.sub,
      (sub) => {
        this.startIdleMonitor(sub)
      },
      { immediate: true }
    )

    $("form").on('submit', (e) => {
      e.preventDefault()
      e.stopPropagation()
    })

    $(window).on('beforeunload', () => {
      this.$store.dispatch("setOriginalObject", null)
      this.$store.dispatch("setNewObject", null)
    });
    $(window).scrollTop(0);
  },
  beforeDestroy() {
    window.removeEventListener('pagehide', this.onPageHide)
    window.removeEventListener('pageshow', this.onPageShow)
    this.destroyIdleMonitor()
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
    }
    if (this.deferredSignOutTimer) {
      clearTimeout(this.deferredSignOutTimer)
    }
  },
}
</script>
<style>
.el-form-item__error {
  position: relative !important;
}
.el-input__count {
  line-height: 1;
  bottom: 12px;
}
.custom-popover {
  position: fixed;
  left: 62px;
  bottom: 40px;
}
</style>
